import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Compass, ListBullets, Rainbow } from "@phosphor-icons/react";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd, faqJsonLd } from "@/lib/schema";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

// Deep local-guide hub — the "definitive guide from people who actually host
// here" page. Config-driven (src/data/guides.js) so each city hub shares the
// layout: hero + direct answer, anchor TOC, rich sections with host picks,
// FAQ, and Article/FAQPage JSON-LD for AI answer engines.
export default function GuideHub({ config }) {
  // CTA defaults to the generic stays CTA; a guide can override it (e.g. the
  // cruise guide sends warm readers straight to the cruise landing page).
  const cta = config.cta || {
    h: "Stay where the locals actually host.",
    body: "Pet-friendly suites and a private-pool home, minutes from everything in this guide. Book direct for the best rate.",
    label: "Explore our stays",
    to: "/properties",
  };
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: config.h1,
    description: config.answer,
    about: [config.eyebrow.split("·")[0].trim(), "travel guide", "vacation rentals"],
    author: { "@type": "Organization", name: "Double Rainbow Suites", "@id": `${SITE}/#business` },
    publisher: { "@id": `${SITE}/#business` },
    mainEntityOfPage: `${SITE}/${config.slug}`,
    articleSection: config.sections.map((s) => s.h),
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-testid='guide-hub']"] },
  };

  return (
    <main data-testid="guide-hub" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title={config.metaTitle}
        description={config.metaDescription}
        canonical={`${SITE}/${config.slug}`}
        jsonLd={[
          articleLd,
          faqJsonLd(config.faqs),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Neighborhood Guide", path: "/neighborhood" },
            { name: config.shortTitle, path: `/${config.slug}` },
          ]),
        ]}
      />

      {/* Hero + direct answer */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5 flex items-center gap-2">
            <Compass size={16} weight="fill" /> {config.eyebrow}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            {config.h1}
          </h1>
          <p data-testid="guide-hub-answer" className="text-lg md:text-xl text-emerald-soft font-light leading-relaxed max-w-3xl mt-7">
            {config.answer}
          </p>
        </motion.div>

        {/* Anchor table of contents */}
        <motion.nav {...fade(0.1)} aria-label="In this guide" data-testid="guide-hub-toc" className="mt-10 rounded-3xl border border-emerald/12 bg-sand-2 p-7 md:p-8">
          <p className="overline text-sunset mb-4 flex items-center gap-2">
            <ListBullets size={15} weight="bold" /> In this guide
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
            {config.sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`guide-hub-toc-${s.id}`}
                className="text-emerald underline decoration-sunset/40 decoration-1 underline-offset-4 transition-colors duration-300 hover:text-sunset"
              >
                {s.h}
              </a>
            ))}
          </div>
        </motion.nav>
      </div>

      {/* Sections */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-16 md:mt-20 flex flex-col gap-14 md:gap-16">
        {config.sections.map((s, i) => (
          <motion.section key={s.id} id={s.id} {...fade()} className="scroll-mt-28 border-t border-emerald/12 pt-8" data-testid={`guide-hub-section-${s.id}`}>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-emerald mb-5">{s.h}</h2>
            {s.paragraphs.map((p, j) => (
              <p key={j} className="text-emerald-soft font-light leading-relaxed mb-4 last:mb-0">{p}</p>
            ))}
            {s.list && (
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-10 gap-y-2.5">
                {s.list.map((item, j) => (
                  <li key={j} className="flex gap-2.5 text-emerald-soft leading-snug">
                    <span className="text-sunset mt-1.5 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.pick && (
              <div className="mt-6 rounded-2xl bg-emerald text-sand p-6 md:p-7 flex gap-4 items-start" data-testid={`guide-hub-pick-${s.id}`}>
                <Rainbow size={22} weight="fill" className="text-gold shrink-0 mt-1" />
                <div>
                  <p className="overline text-gold mb-1.5">Bill &amp; Jonathan's pick</p>
                  <p className="font-light leading-relaxed">{s.pick}</p>
                </div>
              </div>
            )}
            {s.links && (
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
                {s.links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`guide-hub-link-${s.id}-${l.to.replaceAll("/", "")}`}
                    className="group inline-flex items-center gap-1.5 text-sm font-medium text-emerald hover:text-sunset transition-colors duration-300"
                  >
                    {l.label}
                    <ArrowUpRight size={15} weight="bold" className="text-sunset transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            )}
          </motion.section>
        ))}
      </div>

      {/* FAQ */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <motion.h2 {...fade()} className="font-display text-3xl md:text-4xl tracking-tight mb-8">
          Frequently asked
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-9">
          {config.faqs.map((f, i) => (
            <motion.div key={i} {...fade((i % 2) * 0.06)} className="border-t border-emerald/15 pt-6" data-testid={`guide-hub-faq-${i}`}>
              <h3 className="font-display text-lg md:text-xl leading-snug text-emerald mb-3">{f.q}</h3>
              <p className="text-emerald-soft font-light leading-relaxed">{f.a}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Cross-link to the other hub + CTA */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32 flex flex-col gap-8">
        <motion.div {...fade()}>
          <Link
            to={config.otherHub.to}
            data-testid="guide-hub-other"
            className="group flex items-center justify-between gap-6 rounded-3xl border border-emerald/12 bg-sand-2 p-8 md:p-10 transition-colors duration-300 hover:border-sunset/40"
          >
            <div>
              <p className="overline text-sunset mb-2">Keep exploring</p>
              <p className="font-display text-2xl md:text-3xl text-emerald leading-snug">{config.otherHub.label}</p>
            </div>
            <ArrowRight size={26} weight="bold" className="shrink-0 text-sunset transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </motion.div>
        <motion.div {...fade()} className="rounded-3xl bg-emerald text-sand p-10 md:p-14 text-center" data-testid="guide-hub-cta">
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight max-w-2xl mx-auto">
            {cta.h}
          </h2>
          <p className="text-sand/70 mt-4 max-w-xl mx-auto font-light">
            {cta.body}
          </p>
          <Link
            to={cta.to}
            data-testid="guide-hub-cta-book"
            className="group inline-flex items-center gap-2 mt-8 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-gold hover:text-emerald"
          >
            {cta.label}
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
